import { useMemo } from 'react';
import { useT } from '../../../translation/src';
import { ReferralRole } from '../../../types/graphqlTypes';
import { ordersForCustomers } from './partnerRoles';

/**
 * The partner program's words, one set per kind of partner, so every page says the same:
 * - a reseller or full-service partner orders for its customers at a discount: customer, order, discount, your price;
 * - a lead generator brings in customers and earns a commission: customer, sale, commission, your commission.
 */
export interface PartnerTerms {
  // Whether the words are a reseller's or full-service partner's (orders) rather than a lead generator's (sales).
  orders: boolean;
  deals: string;
  manageDeals: string;
  // The column saying what was ordered or sold.
  dealt: string;
  firstDeal: string;
  share: string;
  yourShare: string;
  shareInTotal: string;
}

export const usePartnerTerms = (role: ReferralRole | undefined): PartnerTerms => {
  const orders = ordersForCustomers(role);
  const ordersLabel = useT('orders', { swc: true });
  const salesLabel = useT('sales', { swc: true });
  const manageOrders = useT('manage orders', { swc: true });
  const manageSales = useT('manage sales', { swc: true });
  const ordered = useT('ordered', { swc: true });
  const sold = useT('sold', { swc: true });
  const firstOrder = useT('first order');
  const firstSale = useT('first sale');
  const discount = useT('discount', { swc: true });
  const commission = useT('commission', { swc: true });
  const yourPrice = useT('your price', { swc: true });
  const yourCommission = useT('your commission', { swc: true });
  const discountInTotal = useT('discount in total', { swc: true });
  const commissionInTotal = useT('commission in total', { swc: true });
  return useMemo(
    () =>
      orders
        ? { orders, deals: ordersLabel, manageDeals: manageOrders, dealt: ordered, firstDeal: firstOrder, share: discount, yourShare: yourPrice, shareInTotal: discountInTotal }
        : { orders, deals: salesLabel, manageDeals: manageSales, dealt: sold, firstDeal: firstSale, share: commission, yourShare: yourCommission, shareInTotal: commissionInTotal },
    [orders, ordersLabel, salesLabel, manageOrders, manageSales, ordered, sold, firstOrder, firstSale, discount, commission, yourPrice, yourCommission, discountInTotal, commissionInTotal],
  );
};
